/*
  BC quick-buy popup: Popup Description + Convermax fitment check.
  Added 2026-10-08, Theme Development. Jason's call: WARN only, never block Add to cart.

  Why this exists: Impact's quick-buy drawer injects the product HTML without running
  its scripts, so the Convermax verify-fitment app block inside it stays an empty grey
  box. This script fills that box from attributes set on <quick-buy-drawer> in
  snippets/horizontal-product.liquid:
    data-bc-desc        custom.popup_description (plain text)
    data-bc-fitment     present when the product has a bike-specific Convermax fitment list
    data-bc-product-id  Shopify product id, passed to Convermax.verifyFitmentAsync
  Box hidden when there is neither a description nor a fitment list.
*/
(() => {
  if (window.bcQuickBuyFitment) return;
  window.bcQuickBuyFitment = true;

  const style = document.createElement('style');
  style.textContent = `
    quick-buy-drawer .bc-qb-box { display: block; padding: 16px 12px; background: rgb(231, 231, 231); }
    quick-buy-drawer .bc-qb-box[hidden] { display: none; }
    quick-buy-drawer .bc-qb-desc { margin: 0; }
    quick-buy-drawer .bc-qb-desc + .bc-qb-fit { margin-top: 10px; }
    quick-buy-drawer .bc-qb-fit { font-weight: 600; }
    quick-buy-drawer .bc-qb-fit a { font-weight: 600; text-decoration: underline; }
    /* 2026-10-09, Jason: centre the quick-buy popup on every screen.
       While open, Impact's JS sets inline "display: block" on the drawer (and "display: none"
       once closed), so that attribute is the open-state hook. The drawer becomes a full-screen
       flex box that centres its content panel. No fit-content height (it collapsed the popup
       on iOS Safari) and no transform (the overlay is position: fixed inside the shadow root).
       !important beats Impact's inline right/bottom/top positioning. */
    quick-buy-drawer.quick-buy-drawer[style*="display: block"] {
      display: flex !important;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      inset: 0 !important;
      width: auto;
      height: auto;
      padding: 12px;
    }
    quick-buy-drawer.quick-buy-drawer::part(content) {
      width: 100%;
      max-width: 560px;
      height: auto;
      max-height: calc(100dvh - 96px);
    }
    quick-buy-drawer.quick-buy-drawer::part(body) {
      min-height: 0;
      flex: 1 1 auto;
    }
    quick-buy-drawer.quick-buy-drawer::part(outside-close-button) {
      flex-shrink: 0;
    }
  `;
  document.head.appendChild(style);

  const cm = () => window.Convermax;
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function vehicleString() {
    try { return cm()?.isVehicleSelected?.() ? String(cm().getVehicleString() || '').trim() : ''; } catch (e) { return ''; }
  }

  // Picking a bike happens on the full product page, never in the popup: Convermax's
  // vehicle dialog redirects to the bike's collection on select (tested 2026-10-08, even
  // with config.isAutoRedirectDisabled), while the page's verify-fitment widget does not.
  const productLink = (drawer, text) =>
    `<a href="${esc(`${window.Shopify?.routes?.root || '/'}products/${drawer.getAttribute('handle')}`)}#cm-verify-fitment">${text}</a>`;

  function fallback(fit, drawer) {
    fit.dataset.state = 'fallback';
    fit.innerHTML = `Fitment varies by bike. ${productLink(drawer, 'Check fitment on the full product page')}`;
  }

  async function check(fit, drawer) {
    const api = cm();
    const vehicle = vehicleString();
    fit.dataset.vehicle = vehicle;
    fit.dataset.api = api?.verifyFitmentAsync ? '1' : '0';

    if (!api?.verifyFitmentAsync) return fallback(fit, drawer);

    if (!vehicle) {
      fit.dataset.state = 'select';
      fit.innerHTML = `Fitment varies by bike. ${productLink(drawer, 'Select your bike on the full product page')}`;
      return;
    }

    let result;
    try { result = await api.verifyFitmentAsync(String(drawer.dataset.bcProductId)); } catch (e) { result = null; }
    if (fit.dataset.vehicle !== vehicle) return; // vehicle changed mid-check, the poll re-runs it

    if (result === 'yes' || result === 'universal') {
      fit.dataset.state = 'yes';
      fit.innerHTML = `&#10003; Fits your ${esc(vehicle)}`;
    } else if (result === 'no') {
      fit.dataset.state = 'no';
      fit.innerHTML = `&#10005; Does not fit your ${esc(vehicle)}. ${productLink(drawer, 'Check another bike')}`;
    } else {
      fallback(fit, drawer);
    }
  }

  function fill(drawer, cmBox) {
    cmBox.dataset.bcDone = 'true';
    const desc = drawer.dataset.bcDesc || '';
    const hasFitment = drawer.hasAttribute('data-bc-fitment');
    const holder = cmBox.closest('.shopify-app-block') || cmBox;

    // Drop the Convermax id: the main product page owns #cm-verify-fitment.
    cmBox.removeAttribute('id');
    cmBox.classList.remove('cm-verify-fitment');
    cmBox.classList.add('bc-qb-box');

    if (!desc && !hasFitment) {
      holder.style.display = 'none';
      return;
    }

    cmBox.innerHTML = '';
    if (desc) {
      const p = document.createElement('p');
      p.className = 'bc-qb-desc';
      p.textContent = desc;
      cmBox.appendChild(p);
    }
    if (hasFitment) {
      const fit = document.createElement('div');
      fit.className = 'bc-qb-fit';
      fit.setAttribute('aria-live', 'polite');
      cmBox.appendChild(fit);
      check(fit, drawer);
    }
  }

  function scan() {
    document.querySelectorAll('quick-buy-drawer .cm-verify-fitment:not([data-bc-done])').forEach((box) => {
      fill(box.closest('quick-buy-drawer'), box);
    });
  }

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    // setTimeout, not requestAnimationFrame: rAF pauses while the tab is not painting.
    setTimeout(() => { queued = false; scan(); }, 0);
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Convermax has no change event on its public API, so re-check open popups when the vehicle changes.
  setInterval(() => {
    const apiNow = cm()?.verifyFitmentAsync ? '1' : '0';
    document.querySelectorAll('quick-buy-drawer .bc-qb-fit').forEach((fit) => {
      if (fit.dataset.vehicle !== vehicleString() || fit.dataset.api !== apiNow) check(fit, fit.closest('quick-buy-drawer'));
    });
  }, 1000);
})();
